// ─── Chapter Store ────────────────────────────────────────────────────────────
// Single canonical source of truth for chapter workflow state across the app.
// All UI components that display chapter status must subscribe here.
//
// Write path:  applyWorkflowTransition → refreshChapter → store update → all re-renders
// Read path:   useChapterStore(state => state.chapters[id]?.status)

import { create } from 'zustand'
import { supabase } from '../lib/supabase'

export const useChapterStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  chapters: {},       // chapterId → ChapterRow (authoritative)
  refreshing: {},     // chapterId → boolean (loading indicator)

  // ── Hydrate ───────────────────────────────────────────────────────────────
  // Called on page load to populate the store from already-fetched DB rows.

  hydrate(chapterList) {
    if (!chapterList?.length) return
    const incoming = {}
    for (const ch of chapterList) {
      if (ch?.id) incoming[ch.id] = ch
    }
    set(state => ({ chapters: { ...state.chapters, ...incoming } }))
  },

  // ── Optimistic patch ──────────────────────────────────────────────────────
  // Immediate UI update before the authoritative re-fetch completes.
  // Always follow with refreshChapter for consistency.

  patch(chapterId, fields) {
    if (!chapterId) return
    set(state => {
      const existing = state.chapters[chapterId]
      if (!existing) return state
      return {
        chapters: { ...state.chapters, [chapterId]: { ...existing, ...fields } },
      }
    })
  },

  // ── Authoritative refresh ─────────────────────────────────────────────────
  // Re-fetches the chapter row from DB and writes to store.
  // Called after EVERY workflow transition. This is the canonical sync point.

  async refreshChapter(chapterId) {
    if (!chapterId) return null
    set(state => ({ refreshing: { ...state.refreshing, [chapterId]: true } }))
    try {
      const { data, error } = await supabase
        .from('chapters')
        .select('*')
        .eq('id', chapterId)
        .maybeSingle()

      if (data && !error) {
        set(state => ({
          chapters:   { ...state.chapters, [data.id]: data },
          refreshing: { ...state.refreshing, [chapterId]: false },
        }))
        return data
      }
    } catch { /* network error — don't clear existing state */ }

    set(state => ({ refreshing: { ...state.refreshing, [chapterId]: false } }))
    return null
  },

  // ── Refresh all chapters for a thesis ─────────────────────────────────────
  // Called from ThesisExportPage after any transition to refresh the sidebar.

  async refreshThesisChapters(thesisId) {
    if (!thesisId) return []
    const { data } = await supabase
      .from('chapters')
      .select('*')
      .eq('thesis_id', thesisId)
      .order('chapter_number')

    if (data?.length) {
      const incoming = {}
      for (const ch of data) incoming[ch.id] = ch
      set(state => ({ chapters: { ...state.chapters, ...incoming } }))
    }
    return data || []
  },

  // ── Selectors (callable outside React) ───────────────────────────────────

  getStatus(chapterId) {
    return get().chapters[chapterId]?.status ?? null
  },

  getChapter(chapterId) {
    return get().chapters[chapterId] ?? null
  },

  getThesisChapters(thesisId) {
    return Object.values(get().chapters)
      .filter(c => c.thesis_id === thesisId)
      .sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
  },
}))
